import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtTime, fileUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { StatusDot, StatCard, PageHeader } from "@/components/bits";
import PairingDialog from "@/components/PairingDialog";
import { Plus, MonitorSmartphone, Trash2, Send } from "lucide-react";
import { toast } from "sonner";

const BULK_LABELS = { SYNC: "Sync", RELOAD: "Reload", RESTART: "Neustart (App)", REBOOT: "Reboot (Gerät)", SCREENSHOT: "Screenshot" };

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [pairOpen, setPairOpen] = useState(false);
  const [fleetResetArmed, setFleetResetArmed] = useState(false);
  const [resetArmedId, setResetArmedId] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");
  const [bulkType, setBulkType] = useState("SYNC");
  const [bulkArmed, setBulkArmed] = useState(false);

  async function deviceFactoryReset(d) {
    try {
      await api.post(`/devices/${d.id}/commands`, { type: "FACTORY_RESET" });
      toast.success(`„${d.display_name || d.label || "Gerät"}" wird auf Werkseinstellungen zurückgesetzt`);
      setDevices((ds) => ds.filter((x) => x.id !== d.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Zurücksetzen");
    } finally {
      setResetArmedId(null);
    }
  }

  async function fleetResetAll() {
    try {
      const { data } = await api.post("/devices/factory-reset-all");
      toast.success(`Factory Reset an ${data.enqueued} Geräte gesendet`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Flotten-Reset");
    } finally {
      setFleetResetArmed(false);
    }
  }

  async function sendBulk() {
    try {
      const { data } = await api.post("/commands/bulk", { type: bulkType, tenant_id: tenantFilter || null, confirm_fleet: !tenantFilter });
      toast.success(`${BULK_LABELS[bulkType]} an ${data.enqueued} Gerät${data.enqueued === 1 ? "" : "e"} gesendet`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Massen-Befehl");
    } finally {
      setBulkArmed(false);
    }
  }

  const load = useCallback(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/devices").then((r) => setDevices(r.data)).catch(() => {});
    if (user?.role === "SUPER_ADMIN") {
      api.get("/tenants").then((r) => setTenants(r.data)).catch(() => {});
    }
  }, [user?.role]);

  const filtered = tenantFilter ? devices.filter((d) => d.tenant_id === tenantFilter) : devices;

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div data-testid="dashboard">
      <PageHeader title="Geräteübersicht" sub="Live-Übersicht aller Displays und Standorte">
        {user?.role === "SUPER_ADMIN" &&
          (fleetResetArmed ? (
            <>
              <button onClick={() => setFleetResetArmed(false)} className="btn-ghost text-xs" data-testid="dashboard-factory-reset-cancel">
                Abbrechen
              </button>
              <button
                onClick={fleetResetAll}
                className="inline-flex items-center justify-center rounded-sm bg-red-500 px-3 py-2 text-xs font-semibold text-white hover:bg-red-400 transition-colors"
                data-testid="dashboard-factory-reset-confirm"
              >
                Alle Geräte endgültig zurücksetzen
              </button>
            </>
          ) : (
            <button
              onClick={() => setFleetResetArmed(true)}
              className="btn-ghost !border-red-500/40 !text-red-400 hover:!bg-red-500/10"
              title="Factory Reset an alle gekoppelten Geräte senden"
              data-testid="dashboard-factory-reset-all"
            >
              Flotten-Reset
            </button>
          ))}
        <button onClick={() => setPairOpen(true)} className="btn-primary" data-testid="dashboard-add-device-button">
          <Plus size={15} /> Gerät koppeln
        </button>
      </PageHeader>

      <div className="panel px-4 py-3 mb-6 flex flex-wrap items-center gap-3" data-testid="dashboard-bulk-bar">
        <span className="mono-label flex items-center gap-1.5"><Send size={11} /> Massen-Befehl</span>
        {user?.role === "SUPER_ADMIN" && (
          <select
            value={tenantFilter}
            onChange={(e) => { setTenantFilter(e.target.value); setBulkArmed(false); }}
            className="input-dark !w-auto !py-1.5 text-xs"
            data-testid="dashboard-tenant-filter"
          >
            <option value="">Alle Mandanten</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        )}
        <select
          value={bulkType}
          onChange={(e) => { setBulkType(e.target.value); setBulkArmed(false); }}
          className="input-dark !w-auto !py-1.5 text-xs"
          data-testid="dashboard-bulk-type"
        >
          {Object.entries(BULK_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        {bulkArmed ? (
          <>
            <button onClick={() => setBulkArmed(false)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="dashboard-bulk-cancel">
              Abbrechen
            </button>
            <button
              onClick={sendBulk}
              className="inline-flex items-center justify-center rounded-sm bg-amber-500 px-3 py-1.5 text-xs font-semibold text-black hover:bg-amber-400 transition-colors"
              data-testid="dashboard-bulk-confirm"
            >
              {BULK_LABELS[bulkType]} an {filtered.length} Gerät{filtered.length === 1 ? "" : "e"} senden
            </button>
          </>
        ) : (
          <button onClick={() => setBulkArmed(true)} className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="dashboard-bulk-send">
            Ausführen
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="dashboard-stats">
        <StatCard label="Geräte gesamt" value={data ? data.devices_total : "—"} testid="stat-devices-total" />
        <StatCard label="Online" value={data ? data.online : "—"} tone="text-emerald-400" testid="stat-online" />
        <StatCard label="Offline" value={data ? data.offline : "—"} tone="text-red-400" testid="stat-offline" />
        <StatCard label="Im Setup" value={data ? data.setup : "—"} tone="text-amber-400" testid="stat-setup" />
        <StatCard label="Medien" value={data ? data.media_total : "—"} testid="stat-media" />
        <StatCard label="Playlists" value={data ? data.playlists_total : "—"} testid="stat-playlists" />
        {user?.role === "SUPER_ADMIN" && (
          <StatCard label="Mandanten" value={data ? data.tenants_total : "—"} tone="text-cyan-400" testid="stat-tenants" />
        )}
      </div>

      <div>
          {filtered.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="dashboard-device-tiles">
              {filtered.map((d) => (
                <Link
                  key={d.id}
                  to={`/devices/${d.id}`}
                  className="panel relative p-4 hover:border-cyan-500/40 hover:-translate-y-0.5 transition-all duration-200 block"
                  data-testid={`device-tile-${d.id}`}
                >
                  <div className="flex gap-3">
                    <div
                      className="w-24 h-14 rounded-sm overflow-hidden bg-black border border-border shrink-0 flex items-center justify-center"
                      data-testid={`device-thumb-${d.id}`}
                    >
                      {d.thumb_url ? (
                        <img src={fileUrl(d.thumb_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <MonitorSmartphone size={16} className="text-slate-700" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-display font-semibold text-slate-100 truncate">
                            {d.display_name || d.label || "Unbenanntes Gerät"}
                          </div>
                          <div className="font-mono text-[10px] text-slate-400 mt-0.5">
                            {d.id.slice(0, 13)}… · Aufl.{" "}
                            <span className="text-slate-100 font-semibold" data-testid={`device-resolution-${d.id}`}>
                              {d.state?.screen_width
                                ? (d.rotation || 0) % 180 !== 0
                                  ? `${d.state.screen_height} × ${d.state.screen_width}`
                                  : `${d.state.screen_width} × ${d.state.screen_height}`
                                : "—"}
                            </span>
                          </div>
                        </div>
                        <StatusDot online={d.online} status={d.status} />
                      </div>
                      <div className="mt-2 grid grid-cols-2 gap-x-3 text-xs">
                        <div className="truncate text-slate-400">
                          <span className="mono-label mr-1.5">Ort</span>
                          {d.location_name || "—"}
                        </div>
                        <div className="truncate text-slate-400">
                          <span className="mono-label mr-1.5">Liste</span>
                          {d.playlist_name || "—"}
                        </div>
                      </div>
                    </div>
                  </div>
                  {resetArmedId === d.id ? (
                    <div
                      className="mt-4 pt-3 border-t border-red-500/30 flex items-center justify-between gap-2"
                      data-testid={`device-reset-armed-${d.id}`}
                    >
                      <span className="font-mono text-[10px] uppercase tracking-widest text-red-400">
                        Auf Werkseinstellungen zurücksetzen?
                      </span>
                      <div className="flex gap-1.5 shrink-0">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setResetArmedId(null);
                          }}
                          className="btn-ghost !py-1 !px-2 text-[10px]"
                          data-testid={`device-reset-cancel-${d.id}`}
                        >
                          Nein
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            deviceFactoryReset(d);
                          }}
                          className="inline-flex items-center rounded-sm bg-red-500 px-2 py-1 text-[10px] font-semibold text-white hover:bg-red-400"
                          data-testid={`device-reset-confirm-${d.id}`}
                        >
                          Ja, zurücksetzen
                        </button>
                      </div>
                    </div>
                  ) : d.state?.temp_c != null && d.online ? (
                    <div className="mt-4 pt-3 border-t border-border/60 flex gap-4 pr-6 font-mono text-[10px] text-slate-300">
                      <span>CPU {d.state.cpu_percent}%</span>
                      <span>RAM {d.state.mem_percent}%</span>
                      <span>{d.state.temp_c}°C</span>
                    </div>
                  ) : (
                    <div className="mt-4 pt-3 border-t border-border/60 pr-6 font-mono text-[10px] text-slate-400">
                      Letzter Kontakt: {fmtTime(d.state?.last_seen_at || d.last_seen_at)}
                    </div>
                  )}
                  {resetArmedId !== d.id && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setResetArmedId(d.id);
                      }}
                      className="absolute bottom-3 right-3 p-1 text-slate-700 hover:text-red-400 transition-colors"
                      title="Auf Werkseinstellungen zurücksetzen"
                      data-testid={`device-reset-${d.id}`}
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </Link>
              ))}
            </div>
          ) : (
            <div className="panel p-10 text-center" data-testid="dashboard-no-devices">
              <div className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-4">
                {tenantFilter ? "Keine Geräte bei diesem Mandanten" : "Noch keine Geräte registriert"}
              </div>
              <button onClick={() => setPairOpen(true)} className="btn-primary" data-testid="dashboard-empty-pair-button">
                <Plus size={15} /> Erstes Gerät koppeln
              </button>
            </div>
          )}
      </div>

      <PairingDialog open={pairOpen} onOpenChange={setPairOpen} onPaired={load} />
    </div>
  );
}
